// IndexedDB persistence layer (via the `idb` wrapper). This is the app's only
// source of truth — src/state.ts hydrates an in-memory mirror from these stores on
// boot and every mutation writes through here before the UI re-renders, so all
// state survives app/browser restarts (build brief "known gaps" #1, spec §19 rule 14).
//
// Photo original + thumbnail blobs are also kept in IndexedDB (as Blob values),
// which is a durable, app-private, browser-managed storage mechanism — satisfying
// the brief's "OPFS or Cache Storage for photo blobs" intent without OPFS's much
// narrower browser support. See PHOTO_STORAGE_NOTE below.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Trip, Category, CurrencyDef, Expense, Photo, Meta, SyncQueueItem } from '../types';

export const PHOTO_STORAGE_NOTE =
  'Photos are stored as Blobs inside IndexedDB, a durable, app-private, origin-scoped ' +
  'browser store — not a plain filesystem folder. This abstracts the platform storage ' +
  "detail away from the user, who just sees a private Trip Photos collection, per spec §4.";

interface RoadtripDB extends DBSchema {
  trip: { key: string; value: Trip };
  categories: { key: string; value: Category; indexes: { byOrder: number } };
  currencies: { key: string; value: CurrencyDef };
  expenses: { key: string; value: Expense; indexes: { byDate: string } };
  photos: { key: string; value: Photo; indexes: { byCapturedAt: string } };
  photoBlobs: { key: string; value: { id: string; original: Blob; thumbnail: Blob } };
  meta: { key: string; value: Meta };
  syncQueue: { key: string; value: SyncQueueItem; indexes: { byCreatedAt: string } };
}

const DB_NAME = 'roadtrip';
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<RoadtripDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<RoadtripDB>> {
  if (!dbPromise) {
    dbPromise = openDB<RoadtripDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('trip', { keyPath: 'id' });
          const cat = db.createObjectStore('categories', { keyPath: 'id' });
          cat.createIndex('byOrder', 'sortOrder');
          db.createObjectStore('currencies', { keyPath: 'code' });
          const exp = db.createObjectStore('expenses', { keyPath: 'id' });
          exp.createIndex('byDate', 'date');
          const ph = db.createObjectStore('photos', { keyPath: 'id' });
          ph.createIndex('byCapturedAt', 'capturedAt');
          db.createObjectStore('photoBlobs', { keyPath: 'id' });
          db.createObjectStore('meta', { keyPath: 'key' });
        }
        if (oldVersion < 2) {
          // Queue of failed server-mirror POSTs (see lib/serverMirror.ts) — retried
          // once the app is back online. Not part of the app's real data model.
          const sq = db.createObjectStore('syncQueue', { keyPath: 'id' });
          sq.createIndex('byCreatedAt', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
}

/* ---------- trip (singleton) ---------- */
export async function getTrip(): Promise<Trip | undefined> {
  return (await getDB()).get('trip', 'trip');
}
export async function putTrip(trip: Trip): Promise<void> {
  await (await getDB()).put('trip', trip);
}

/* ---------- categories ---------- */
export async function getAllCategories(): Promise<Category[]> {
  const all = await (await getDB()).getAllFromIndex('categories', 'byOrder');
  return all;
}
export async function putCategory(c: Category): Promise<void> {
  await (await getDB()).put('categories', c);
}
export async function deleteCategory(id: string): Promise<void> {
  await (await getDB()).delete('categories', id);
}

/* ---------- currencies ---------- */
export async function getAllCurrencies(): Promise<CurrencyDef[]> {
  return (await getDB()).getAll('currencies');
}
export async function putCurrency(c: CurrencyDef): Promise<void> {
  await (await getDB()).put('currencies', c);
}
export async function deleteCurrency(code: string): Promise<void> {
  await (await getDB()).delete('currencies', code);
}

/* ---------- expenses ---------- */
export async function getAllExpenses(): Promise<Expense[]> {
  return (await getDB()).getAll('expenses');
}
export async function putExpense(e: Expense): Promise<void> {
  await (await getDB()).put('expenses', e);
}
export async function deleteExpense(id: string): Promise<void> {
  await (await getDB()).delete('expenses', id);
}

/* ---------- photos + blobs ---------- */
export async function getAllPhotos(): Promise<Photo[]> {
  return (await getDB()).getAll('photos');
}
export async function putPhoto(p: Photo, blobs?: { original: Blob; thumbnail: Blob }): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['photos', 'photoBlobs'], 'readwrite');
  await tx.objectStore('photos').put(p);
  if (blobs) {
    await tx.objectStore('photoBlobs').put({ id: p.id, ...blobs });
  }
  await tx.done;
}
export async function deletePhoto(id: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['photos', 'photoBlobs'], 'readwrite');
  await tx.objectStore('photos').delete(id);
  await tx.objectStore('photoBlobs').delete(id);
  await tx.done;
}
export async function getPhotoBlob(
  id: string,
  variant: 'original' | 'thumbnail'
): Promise<Blob | undefined> {
  const row = await (await getDB()).get('photoBlobs', id);
  return row?.[variant];
}

/* ---------- meta (small key/value bag: lastCurrency, mapView, reportCurrencyMode…) ---------- */
export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const row = await (await getDB()).get('meta', key);
  return row?.value as T | undefined;
}
export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await getDB()).put('meta', { key, value });
}

export async function isDatabaseEmpty(): Promise<boolean> {
  return (await getTrip()) === undefined;
}

/* ---------- sync queue (server-mirror retry, see lib/serverMirror.ts) ---------- */
export async function queueSync(item: SyncQueueItem): Promise<void> {
  await (await getDB()).put('syncQueue', item);
}
export async function getAllSyncQueue(): Promise<SyncQueueItem[]> {
  return (await getDB()).getAllFromIndex('syncQueue', 'byCreatedAt');
}
export async function deleteSyncItem(id: string): Promise<void> {
  await (await getDB()).delete('syncQueue', id);
}
export async function bumpSyncAttempts(id: string): Promise<void> {
  const database = await getDB();
  const item = await database.get('syncQueue', id);
  if (item) {
    item.attempts += 1;
    await database.put('syncQueue', item);
  }
}
